import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { TrendingUp, TrendingDown, BarChart3, DollarSign } from 'lucide-react';
import { calculateWinRatePercent } from '../../lib/trades/winRate';

interface Trade {
    id: number;
    date: string;
    symbol: string;
    volume: number;
    executions: number;
    pnl: number;
    side: 'Long' | 'Short';
    status: 'open' | 'closed';
}

interface EnhancedStatsProps {
    trades: Trade[];
    totalPnl: number;
}

const fmtMoney = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        signDisplay: 'auto',
    }).format(amount);
};

const COLORS = {
    win: '#10b981',
    loss: '#ef4444',
    neutral: '#6b7280'
};

export function EnhancedStats({ trades, totalPnl }: EnhancedStatsProps) {
    const winningTrades = trades.filter(t => t.pnl > 0);
    const losingTrades = trades.filter(t => t.pnl < 0);
    const winRate = calculateWinRatePercent(winningTrades.length, losingTrades.length);
    const rateDenominator = winningTrades.length + losingTrades.length;
    const lossRate = rateDenominator > 0 ? (losingTrades.length / rateDenominator) * 100 : 0;
    const totalVolume = trades.reduce((sum, trade) => sum + trade.volume, 0);
    const grossPnl = totalPnl; // Assuming no fees for now
    
    // Mock commission calculation
    const commission = trades.length * 2; // $2 per trade
    const netPnl = grossPnl - commission;
    
    // Mock drawdown data
    const drawdownData = [
        { name: 'Jan', value: -200 },
        { name: 'Feb', value: -150 },
        { name: 'Mar', value: -300 },
        { name: 'Apr', value: -100 },
        { name: 'May', value: -250 },
    ];
    
    const pieData = [
        { name: 'Winners', value: winningTrades.length, color: COLORS.win },
        { name: 'Losers', value: losingTrades.length, color: COLORS.loss }
    ];
    
    return (
        <div className="space-y-6">
            {/* Main P&L Display */}
            <div className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 p-6">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-zinc-500 dark:text-zinc-400">Gross P&L</span>
                    <BarChart3 className="w-4 h-4 text-zinc-400" />
                </div>
                <div className={`text-3xl font-bold ${totalPnl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {fmtMoney(grossPnl)}
                </div>
            </div>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
                {/* Total Trades & Win Rate */}
                <div className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 p-4">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-zinc-500 dark:text-zinc-400">Total Trades:</span>
                        <span className="text-lg font-semibold">{trades.length}</span>
                    </div>
                    
                    {/* Win/Loss Donut Chart */}
                    <div className="flex items-center gap-4">
                        <div className="w-24 h-24">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={30}
                                        outerRadius={45}
                                        dataKey="value"
                                        strokeWidth={0}
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="flex-1">
                            <div className="text-xs text-zinc-500 dark:text-zinc-400 mb-1">
                                <span className="inline-flex items-center gap-1">
                                    <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                                    {winningTrades.length} Winners
                                </span>
                            </div>
                            <div className="text-xs text-zinc-500 dark:text-zinc-400">
                                <span className="inline-flex items-center gap-1">
                                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                    {losingTrades.length} Losers
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Win % & Progress Bar */}
                <div className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 p-4">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-zinc-500 dark:text-zinc-400">Win %: Trades:</span>
                        <span className="text-lg font-semibold text-emerald-500">{winRate.toFixed(1)}%</span>
                    </div>
                    
                    {/* Win Rate Progress Bar */}
                    <div className="space-y-2">
                        <div className="flex justify-between text-xs text-zinc-500 dark:text-zinc-400">
                            <span>Loss %</span>
                            <span>{lossRate.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-zinc-200 dark:bg-zinc-700 rounded-full h-2">
                            <div 
                                className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${winRate}%` }}
                            ></div>
                        </div>
                        <div className="w-full bg-zinc-200 dark:bg-zinc-700 rounded-full h-2">
                            <div 
                                className="bg-red-500 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${lossRate}%` }}
                            ></div>
                        </div>
                    </div>
                </div>
                
                {/* Volume */}
                <div className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-zinc-500 dark:text-zinc-400">Volume</span>
                        <TrendingUp className="w-4 h-4 text-zinc-400" />
                    </div>
                    <div className="text-lg font-semibold">{totalVolume.toLocaleString()}</div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                        25.0%
                    </div>
                    <div className="w-full bg-zinc-200 dark:bg-zinc-700 rounded-full h-1 mt-2">
                        <div className="bg-blue-500 h-1 rounded-full" style={{ width: '25%' }}></div>
                    </div>
                </div>
                
                {/* Net P&L */}
                <div className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-zinc-500 dark:text-zinc-400">Net P&L</span>
                        <DollarSign className="w-4 h-4 text-zinc-400" />
                    </div>
                    <div className={`text-lg font-semibold ${netPnl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {fmtMoney(netPnl)}
                    </div>
                </div>
            </div>
            
            {/* Max Drawdown & Commission */}
            <div className="grid grid-cols-2 gap-4">
                <div className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 p-4">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-zinc-500 dark:text-zinc-400">Max Drawdown</span>
                        <TrendingDown className="w-4 h-4 text-red-400" />
                    </div>
                    <div className="h-8 mb-2">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={drawdownData}>
                                <Bar dataKey="value" fill="#ef4444" radius={[2, 2, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="text-red-500 font-semibold">-$500</div>
                </div>
                
                <div className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-zinc-500 dark:text-zinc-400">Commission/Fees:</span>
                        <span className="text-sm font-medium text-emerald-500">{fmtMoney(commission)}</span>
                    </div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-2">
                        <div>Commission: $100, execution vs average</div>
                        <div>Transf.ee: $0 $60.80</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
